import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, Plus, Loader2, Search, ChevronDown, ChevronRight, AlertTriangle,
  Box, Droplets, Scissors, Wrench, Layers, HelpCircle, Archive, RotateCcw, Eye, EyeOff, Pencil, Mail, X, Camera,
  Sliders, Trash2, XCircle
} from "lucide-react";
import AdjustStockLevelModal from "./AdjustStockLevelModal";
import SupplierNamesCard from "./SupplierNamesCard";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Switch } from "../ui/switch";
import { API } from "../../App";
import CostSanityWarning from "./CostSanityWarning";
import { SuggestInput } from "./SuggestInput";
import {
  visibleOptionalFields,
  isKeptForExistingValue,
  clearedHiddenFields,
} from "../../utils/stockOptionalFields";

// Category icons
const CATEGORY_ICONS = {
  "Films/Wraps": Box,
  "Chemicals/Liquids": Droplets,
  "Blades/Cutting tools": Scissors,
  "Tools/Equipment": Wrench,
  "Consumables": Layers,
  "Other": HelpCircle
};

// ---------------------------------------------------------------------
// Category-aware optional product attributes. Rules + the category map
// live in utils/stockOptionalFields.js (unit-tested there).
//
// Each field also offers the values already in use as a pick-list
// (SuggestInput) so the regulars don't get re-typed and mis-typed.
// `variant_type` suggestions are scoped to the selected category,
// because the values are category-specific (Ceramic/Performance are
// films, Slip Solution/Primer are chemicals, Squeegee/Blade are tools).
const OptionalFieldsGrid = ({ category, data, onFieldChange, testIdPrefix, suggestions }) => {
  const fields = visibleOptionalFields(category, data);

  const suggestionsFor = (key) => {
    if (key === "variant_type") {
      const byCategory = suggestions?.variant_type || {};
      // No category picked yet (or an unknown one) → offer every known
      // variant rather than an empty list.
      if (category && byCategory[category]) return byCategory[category];
      return Object.values(byCategory).flat();
    }
    return suggestions?.[key] || [];
  };

  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`${testIdPrefix}-optional-fields`}>
      {fields.map(field => {
        const keptForExistingValue = isKeptForExistingValue(category, field.key);
        return (
          <div key={field.key} className="space-y-2">
            <Label className="text-slate-400 text-sm">{field.label}</Label>
            <SuggestInput
              value={data[field.key] || ""}
              onChange={(val) => onFieldChange(field.key, val)}
              suggestions={suggestionsFor(field.key)}
              placeholder={field.placeholder}
              className="bg-slate-800 border-slate-700 text-white text-sm"
              testId={`${testIdPrefix}-${field.key}`}
            />
            {keptForExistingValue && (
              <p
                className="text-xs text-amber-500/80"
                data-testid={`${testIdPrefix}-${field.key}-kept-note`}
              >
                Shown because this item already has a value saved. Not normally
                used for {category || "this category"}.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Helper to format quantity display
const formatQuantity = (amount, baseUnit) => {
  if (baseUnit === "cm") {
    const meters = Math.floor(amount / 100);
    const cm = Math.round(amount % 100);
    if (meters > 0 && cm > 0) return `${meters}m ${cm}cm`;
    if (meters > 0) return `${meters}m`;
    return `${cm}cm`;
  }
  if (baseUnit === "ml") return `${Math.round(amount)}ml`;
  return `${Math.round(amount)} pcs`;
};

const StockInventorySection = ({ isAdmin = false }) => {
  const [products, setProducts] = useState([]);
  const [archivedProducts, setArchivedProducts] = useState([]);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  
  // Add stock modal (for existing products)
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingToProduct, setAddingToProduct] = useState(null);
  const [addQuantityCount, setAddQuantityCount] = useState(1);
  const [addInitialQuantity, setAddInitialQuantity] = useState("");
  const [addCost, setAddCost] = useState("");
  const [addVendor, setAddVendor] = useState("");
  const [addPurchaseDate, setAddPurchaseDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  // VAT treatment + net/gross toggle for Add Stock modal (iter_46g — never
  // save a new costed unit without an explicit VAT classification).
  const [addVatTreatment, setAddVatTreatment] = useState("standard");
  const [addCostMode, setAddCostMode] = useState("net"); // "net" | "gross"
  // Cost sanity: fetched once per opened product; drives the "was this
  // Net or did you accidentally type Gross?" nudge when the typed value
  // drifts >30% from the last-3 net average.
  const [addRecentCosts, setAddRecentCosts] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  // Bulk delivery modal — multi-product intake for a real supplier drop
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryVendor, setDeliveryVendor] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  // Delivery-level VAT settings (one vendor per delivery = one treatment
  // shared by every line — matches how a real supplier invoice works).
  const [deliveryVatTreatment, setDeliveryVatTreatment] = useState("standard");
  const [deliveryCostMode, setDeliveryCostMode] = useState("net");
  const [deliveryLines, setDeliveryLines] = useState([
    { product_id: "", quantity_count: 1, initial_quantity: "", cost: "" },
  ]);
  // Cost-baseline cache keyed by product_id — populated on first
  // product-selection per line so the sanity check has data without
  // repeated round-trips. Empty entry = "still fetching / no data".
  const [costBaselines, setCostBaselines] = useState({});
  // Per-line "yes that's correct" overrides. Keyed by line index so
  // toggling one line doesn't suppress the warning on another.
  const [sanityOverrides, setSanityOverrides] = useState({});
  // Receipts to attach immediately after the delivery is created.
  // Files stay in memory until POST /deliveries returns a delivery_id,
  // then we upload each with a follow-up POST /purchase-receipts/upload.
  const [deliveryReceipts, setDeliveryReceipts] = useState([]);
  const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);
  // Shipping / delivery charge for the whole delivery. Allocated pro-rata
  // across priced lines by line value on the server, so each unit's stored
  // cost becomes true landed cost and margin math picks it up automatically.
  const [deliveryShipping, setDeliveryShipping] = useState("");
  // Explicit "yes, log these without a cost" acknowledgement. Blank-cost
  // lines silently understate every future job's cost, so submitting with
  // one requires ticking this first.
  const [allowMissingCost, setAllowMissingCost] = useState(false);
  // Supplier's printed grand total (goods + shipping + VAT). Optional. On
  // submit the server recomputes the total from what was entered and
  // rejects with a 409 + side-by-side figures if they don't reconcile;
  // ticking the confirm box re-submits with the variance accepted.
  const [deliveryInvoiceTotal, setDeliveryInvoiceTotal] = useState("");
  const [invoiceMismatch, setInvoiceMismatch] = useState(null);
  const [invoiceTotalConfirmed, setInvoiceTotalConfirmed] = useState(false);

  // Cost backfill drawer — retroactively price stock units logged before we
  // started capturing cost. Populated on-demand from GET /units/without-cost.
  const [showBackfillModal, setShowBackfillModal] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillUnits, setBackfillUnits] = useState([]); // [{unit_id, product_name, ...}]
  const [backfillDrafts, setBackfillDrafts] = useState({}); // {unit_id: {cost, vendor_name, purchase_date}}
  const [bulkGroupDrafts, setBulkGroupDrafts] = useState({}); // {product_name: {cost, vendor_name, purchase_date}}
  const [bulkGroupSaving, setBulkGroupSaving] = useState(null); // product_name currently applying
  const [backfillMissingCount, setBackfillMissingCount] = useState(0);
  const [backfillSavingId, setBackfillSavingId] = useState(null);
  // Include used-up (depleted) units so the drawer shows the FULL historical
  // scope of uncosted stock, not just what's still on the shelf.
  const [backfillIncludeDepleted, setBackfillIncludeDepleted] = useState(false);
  const [backfillStats, setBackfillStats] = useState(null);

  // New product modal
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [newItemData, setNewItemData] = useState({
    name: "",
    category: "",
    variant_type: "",
    shade_vlt: "",
    colour: "",
    size: "",
    default_initial_quantity: "",
    default_cost: "",
    low_stock_minimum: ""
  });
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  // Archive confirmation
  const [archivingProduct, setArchivingProduct] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);

  // Adjust Stock Level modal — per-product entry point that lists units
  // and lets the admin correct remaining_quantity on any of them.
  // Distinct from Purchases' Edit-delivery-line (initial_quantity reset).
  const [adjustingProduct, setAdjustingProduct] = useState(null);

  // Hard-delete confirmation (archived products only). Two-step because
  // it's destructive AND may cascade to units; the confirm dialog
  // surfaces the exact impact before the admin commits.
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeletePreview, setHardDeletePreview] = useState(null);
  const [isHardDeleting, setIsHardDeleting] = useState(false);

  // Edit product modal
  const [editingProduct, setEditingProduct] = useState(null);
  const [editData, setEditData] = useState({
    name: "",
    category: "",
    variant_type: "",
    shade_vlt: "",
    colour: "",
    size: "",
    default_initial_quantity: "",
    default_cost: "",
    low_stock_minimum: ""
  });
  const [isEditing, setIsEditing] = useState(false);

  // Low stock email alert
  const [isSendingAlert, setIsSendingAlert] = useState(false);

  // Existing values for the free-text fields, fetched from the DB so the
  // pick-lists self-maintain (a width/supplier typed once appears next time).
  const [suggestions, setSuggestions] = useState({
    variant_type: {}, shade_vlt: [], colour: [], size: [], vendors: [],
  });

  // Categories list
  const CATEGORIES = [    "Films/Wraps",
    "Chemicals/Liquids",
    "Blades/Cutting tools",
    "Tools/Equipment",
    "Consumables",
    "Other"
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const requests = [
        axios.get(`${API}/stock/products`),
        axios.get(`${API}/stock/overview`),
        axios.get(`${API}/stock/products/archived`),
        // Existing values for the free-text fields, so the forms can
        // offer them as a pick-list instead of re-typing them.
        axios.get(`${API}/stock/field-suggestions`).catch(() => ({ data: {} })),
      ];
      // /units/without-cost is admin-only — skip for non-admin to avoid 403 noise.
      if (isAdmin) {
        requests.push(
          axios.get(`${API}/stock/units/without-cost`).catch(() => ({ data: { count: 0 } })),
        );
      }
      const [productsRes, overviewRes, archivedRes, suggestionsRes, missingCostRes] =
        await Promise.all(requests);

      setProducts(productsRes.data || []);
      setOverview(overviewRes.data || {});
      setArchivedProducts(archivedRes.data || []);
      setSuggestions({
        variant_type: suggestionsRes?.data?.variant_type || {},
        shade_vlt: suggestionsRes?.data?.shade_vlt || [],
        colour: suggestionsRes?.data?.colour || [],
        size: suggestionsRes?.data?.size || [],
        vendors: suggestionsRes?.data?.vendors || [],
      });
      setBackfillMissingCount(missingCostRes?.data?.count || 0);

      // Expand first category by default
      if (overviewRes.data?.categories) {
        const cats = Object.keys(overviewRes.data.categories);
        if (cats.length > 0) {
          setExpandedCategories({ [cats[0]]: true });
        }
      }
    } catch (error) {
      console.error("Failed to load stock data:", error);
      toast.error("Failed to load inventory");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Cost Backfill flow ---
  const openBackfillModal = async (includeDepleted = backfillIncludeDepleted) => {
    setShowBackfillModal(true);
    setBackfillLoading(true);
    setBackfillDrafts({});
    setBulkGroupDrafts({});
    try {
      const r = await axios.get(`${API}/stock/units/without-cost`, {
        params: { include_depleted: includeDepleted },
      });
      setBackfillUnits(r.data?.units || []);
      setBackfillStats(r.data || null);
      if (!includeDepleted) setBackfillMissingCount(r.data?.count || 0);
    } catch (error) {
      toast.error("Failed to load units without cost");
    } finally {
      setBackfillLoading(false);
    }
  };

  const toggleBackfillDepleted = async () => {
    const next = !backfillIncludeDepleted;
    setBackfillIncludeDepleted(next);
    await openBackfillModal(next);
  };

  const updateBackfillDraft = (unitId, field, value) => {
    setBackfillDrafts((prev) => ({
      ...prev,
      [unitId]: { ...prev[unitId], [field]: value },
    }));
  };

  const saveBackfillRow = async (unit) => {
    const draft = backfillDrafts[unit.unit_id] || {};
    const parsedCost = parseFloat(draft.cost);
    if (!draft.cost || isNaN(parsedCost) || parsedCost <= 0) {
      toast.error("Enter a positive cost");
      return;
    }
    setBackfillSavingId(unit.unit_id);
    try {
      const payload = { cost: parsedCost };
      if (draft.vendor_name && draft.vendor_name.trim()) payload.vendor_name = draft.vendor_name.trim();
      if (draft.purchase_date) payload.purchase_date = draft.purchase_date;
      await axios.patch(`${API}/stock/units/${unit.unit_id}`, payload);
      toast.success(`Priced ${unit.product_name} — €${parsedCost.toFixed(2)}`);
      // Remove the row optimistically; refresh count.
      setBackfillUnits((prev) => prev.filter((u) => u.unit_id !== unit.unit_id));
      setBackfillMissingCount((prev) => Math.max(0, prev - 1));
      setBackfillDrafts((prev) => {
        const { [unit.unit_id]: _drop, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save cost");
    } finally {
      setBackfillSavingId(null);
    }
  };

  const updateBulkGroupDraft = (productName, field, value) => {
    setBulkGroupDrafts((prev) => ({
      ...prev,
      [productName]: { ...prev[productName], [field]: value },
    }));
  };

  const applyBulkGroup = async (productName, unitsInGroup) => {
    const draft = bulkGroupDrafts[productName] || {};
    const parsedCost = parseFloat(draft.cost);
    if (!draft.cost || isNaN(parsedCost) || parsedCost <= 0) {
      toast.error("Enter a positive cost to apply to the whole group");
      return;
    }
    if (
      !window.confirm(
        `Apply €${parsedCost.toFixed(2)} to all ${unitsInGroup.length} uncosted ` +
        `${productName} unit${unitsInGroup.length === 1 ? "" : "s"}?\n\n` +
        `(Any units of this product that already have a per-row cost stay ` +
        `untouched.)`
      )
    ) {
      return;
    }
    setBulkGroupSaving(productName);
    try {
      const treatment = draft.vat_treatment || "standard";
      const inputMode = draft.input_mode || "net";
      const payload = { product_name: productName, vat_treatment: treatment };
      // For standard purchases the user can input either net or gross; the
      // backend reverse-derives net from gross. For reverse-charge / no_vat
      // the amount is always net (there's no supplier VAT to inc).
      if (treatment === "standard" && inputMode === "gross") {
        payload.cost_gross = parsedCost;
      } else {
        payload.cost = parsedCost;
      }
      if (draft.vendor_name && draft.vendor_name.trim()) payload.vendor_name = draft.vendor_name.trim();
      if (draft.purchase_date) payload.purchase_date = draft.purchase_date;
      const res = await axios.post(`${API}/stock/units/bulk-backfill-cost`, payload);
      const modified = res.data?.modified_count ?? 0;
      toast.success(`Backfilled ${modified} ${productName} unit${modified === 1 ? "" : "s"} @ €${parsedCost.toFixed(2)}`);
      // Drop every unit of this product from the local list
      const droppedIds = new Set(unitsInGroup.map((u) => u.unit_id));
      setBackfillUnits((prev) => prev.filter((u) => !droppedIds.has(u.unit_id)));
      setBackfillMissingCount((prev) => Math.max(0, prev - modified));
      setBulkGroupDrafts((prev) => {
        const { [productName]: _drop, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk backfill failed");
    } finally {
      setBulkGroupSaving(null);
    }
  };

  const sendLowStockAlert = async () => {
    setIsSendingAlert(true);
    try {
      const response = await axios.post(`${API}/stock/check-low-stock`);
      const data = response.data;
      
      if (data.low_stock_count === 0) {
        toast.success("All stock levels are healthy - no alert needed");
      } else if (data.email_sent) {
        toast.success(`Low stock alert sent to ${data.email_recipient} for ${data.low_stock_count} item(s)`);
      } else {
        toast.warning(`${data.low_stock_count} low stock items found but email could not be sent`);
      }
    } catch (error) {
      console.error("Failed to check low stock:", error);
      toast.error("Failed to check stock levels");
    } finally {
      setIsSendingAlert(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const openAddModal = (product) => {
    setAddingToProduct(product);
    setAddQuantityCount(1);
    // Pre-fill cost from the product template's default_cost when set —
    // pure data-entry convenience. Staff can still override per delivery.
    // Never touches material-cost math; that always reads the eventual
    // stock unit's own recorded cost.
    setAddCost(
      product?.default_cost != null && product.default_cost > 0
        ? String(product.default_cost)
        : "",
    );
    setAddVendor("");
    setAddPurchaseDate(new Date().toISOString().slice(0, 10));
    setAddVatTreatment("standard");
    setAddCostMode("net");
    setAddRecentCosts(null);
    // Fire-and-forget: pull the last few net costs for THIS product so
    // the sanity nudge can compare typed values against a real baseline.
    // Admin-only endpoint; silently no-ops for non-admins.
    if (isAdmin && product?.product_id) {
      axios
        .get(`${API}/stock/products/${product.product_id}/recent-costs`, {
          params: { limit: 3 },
        })
        .then((res) => setAddRecentCosts(res.data))
        .catch(() => { /* silent — nudge simply won't render */ });
    }
    // Set default initial quantity from product
    if (product.default_initial_quantity) {
      // Convert from base units to display units for entry
      if (product.base_unit === "cm") {
        setAddInitialQuantity((product.default_initial_quantity / 100).toString()); // cm to meters
      } else {
        setAddInitialQuantity(product.default_initial_quantity.toString());
      }
    } else {
      setAddInitialQuantity("");
    }
    setShowAddModal(true);
  };

  const handleAddStock = async () => {
    if (!addingToProduct || !addInitialQuantity || !addQuantityCount) {
      toast.error("Please fill in all fields");
      return;
    }

    let initialQty = parseFloat(addInitialQuantity);
    if (isNaN(initialQty) || initialQty <= 0) {
      toast.error("Invalid quantity");
      return;
    }

    // Convert meters to cm for films
    if (addingToProduct.base_unit === "cm") {
      initialQty = initialQty * 100;
    }

    const parsedCost = addCost.trim() === "" ? null : parseFloat(addCost);
    if (parsedCost !== null && (isNaN(parsedCost) || parsedCost <= 0)) {
      toast.error("Cost must be a positive number (or leave blank)");
      return;
    }

    setIsAdding(true);
    try {
      // Build the payload — send `cost_gross` for standard+gross mode so
      // the server reverse-derives net; otherwise `cost` (net). Always
      // send `vat_treatment` when there IS a cost so the unit is fully
      // classified from day one (no silent standard-default guessing).
      const payload = {
        product_id: addingToProduct.product_id,
        quantity_count: parseInt(addQuantityCount),
        initial_quantity: initialQty,
        vendor_name: addVendor.trim() || null,
        purchase_date: addPurchaseDate || null,
      };
      if (parsedCost !== null) {
        payload.vat_treatment = addVatTreatment;
        if (addVatTreatment === "standard" && addCostMode === "gross") {
          payload.cost_gross = parsedCost;
        } else {
          payload.cost = parsedCost;
        }
      } else {
        payload.cost = null;
      }
      await axios.post(`${API}/stock/units`, payload);

      toast.success(`Added ${addQuantityCount} ${addingToProduct.container_type}(s) of ${addingToProduct.name}`);
      setShowAddModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add stock");
    } finally {
      setIsAdding(false);
    }
  };

  // ---- Bulk delivery (multi-product intake in one pass) ----
  const openDeliveryModal = () => {
    setDeliveryVendor("");
    setDeliveryDate(new Date().toISOString().slice(0, 10));
    setDeliveryVatTreatment("standard");
    setDeliveryCostMode("net");
    setDeliveryLines([
      { product_id: "", quantity_count: 1, initial_quantity: "", cost: "" },
    ]);
    setDeliveryReceipts([]);
    setDeliveryShipping("");
    setAllowMissingCost(false);
    setDeliveryInvoiceTotal("");
    setInvoiceMismatch(null);
    setInvoiceTotalConfirmed(false);
    setShowDeliveryModal(true);
  };

  const addDeliveryLine = () => {
    setDeliveryLines((prev) => [
      ...prev,
      { product_id: "", quantity_count: 1, initial_quantity: "", cost: "" },
    ]);
  };

  const removeDeliveryLine = (idx) => {
    setDeliveryLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateDeliveryLine = (idx, field, value) => {
    setDeliveryLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
    // Lazy-fetch the cost baseline the first time a product is picked
    // on this line. Cached in state so re-picking the same product is
    // free. Silent failure — sanity warning simply won't render.
    if (field === "product_id" && value && !(value in costBaselines)) {
      setCostBaselines((prev) => ({ ...prev, [value]: null }));  // in-flight sentinel
      axios
        .get(`${API}/stock/products/${value}/cost-baseline`)
        .then((r) => setCostBaselines((prev) => ({ ...prev, [value]: r.data })))
        .catch(() => setCostBaselines((prev) => ({ ...prev, [value]: { insufficient: true } })));
    }
    // Changing product or numbers resets that line's sanity override so
    // an old confirmation doesn't leak onto a new set of values.
    if (field === "product_id" || field === "cost" || field === "initial_quantity") {
      setSanityOverrides((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      // Same reasoning for the invoice-total reconciliation: the numbers
      // just changed, so any previous mismatch/acceptance is stale.
      setInvoiceMismatch(null);
      setInvoiceTotalConfirmed(false);
    }
  };

  const handleSubmitDelivery = async () => {
    const validLines = deliveryLines.filter((l) => l.product_id && l.initial_quantity);
    if (validLines.length === 0) {
      toast.error("Add at least one product line");
      return;
    }

    // Strong prompt (not a hard block): any line without a cost means every
    // job that later consumes it silently reports €0 material cost and an
    // inflated margin. Require an explicit acknowledgement first.
    const uncostedLines = validLines.filter((l) => {
      const c = parseFloat(l.cost);
      return l.cost === "" || isNaN(c) || c <= 0;
    });
    if (uncostedLines.length > 0 && !allowMissingCost) {
      toast.error(
        `${uncostedLines.length} line${uncostedLines.length === 1 ? "" : "s"} has no cost — enter a cost or tick the confirm box below`
      );
      return;
    }

    // Build API payload — convert film meters → cm same as single-unit path.
    // For every line that carries a cost, stamp the delivery-level VAT
    // treatment + net/gross mode. One supplier invoice = one treatment,
    // so it's shared across every line rather than repeated per-row.
    const payload_lines = validLines.map((l) => {
      const prod = products.find((p) => p.product_id === l.product_id);
      let iq = parseFloat(l.initial_quantity);
      if (prod && prod.base_unit === "cm") iq = iq * 100;
      const parsedCost = l.cost === "" ? null : parseFloat(l.cost);
      const hasCost =
        parsedCost !== null && !isNaN(parsedCost) && parsedCost > 0;
      const line = {
        product_id: l.product_id,
        quantity_count: parseInt(l.quantity_count) || 1,
        initial_quantity: iq,
      };
      if (hasCost) {
        line.vat_treatment = deliveryVatTreatment;
        if (deliveryVatTreatment === "standard" && deliveryCostMode === "gross") {
          line.cost_gross = parsedCost;
        } else {
          line.cost = parsedCost;
        }
      } else {
        line.cost = null;
      }
      return line;
    });

    setIsSubmittingDelivery(true);
    try {
      const parsedShipping = deliveryShipping === "" ? null : parseFloat(deliveryShipping);
      const parsedInvoiceTotal =
        deliveryInvoiceTotal === "" ? null : parseFloat(deliveryInvoiceTotal);
      const res = await axios.post(`${API}/stock/deliveries`, {
        vendor_name: deliveryVendor.trim() || null,
        purchase_date: deliveryDate || null,
        lines: payload_lines,
        shipping_cost:
          parsedShipping !== null && !isNaN(parsedShipping) && parsedShipping > 0
            ? parsedShipping
            : null,
        shipping_cost_is_gross:
          deliveryVatTreatment === "standard" && deliveryCostMode === "gross",
        invoice_total:
          parsedInvoiceTotal !== null && !isNaN(parsedInvoiceTotal) && parsedInvoiceTotal > 0
            ? parsedInvoiceTotal
            : null,
        invoice_total_confirmed: invoiceTotalConfirmed,
      });
      const deliveryId = res.data?.delivery_id;

      // Upload any receipts the user attached at intake time. We do these
      // sequentially (not Promise.all) so a single failure surfaces
      // cleanly without hiding other errors.
      let receiptFails = 0;
      if (deliveryId && deliveryReceipts.length > 0) {
        for (const rf of deliveryReceipts) {
          try {
            const form = new FormData();
            form.append("file", rf);
            form.append("delivery_id", deliveryId);
            await axios.post(`${API}/stock/purchase-receipts/upload`, form, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            receiptFails += 1;
            console.warn("Receipt upload failed:", rf.name, err?.response?.data);
          }
        }
      }

      const receiptCount = deliveryReceipts.length;
      if (receiptCount === 0) {
        toast.success(res.data?.message || "Delivery logged");
      } else if (receiptFails === 0) {
        toast.success(
          `${res.data?.message || "Delivery logged"} — ${receiptCount} receipt${receiptCount === 1 ? "" : "s"} attached`
        );
      } else {
        toast.warning(
          `Delivery logged, but ${receiptFails} of ${receiptCount} receipt${receiptCount === 1 ? "" : "s"} failed to upload — retry from the Purchases tab`
        );
      }

      setShowDeliveryModal(false);
      setDeliveryShipping("");
      setAllowMissingCost(false);
      setDeliveryInvoiceTotal("");
      setInvoiceMismatch(null);
      setInvoiceTotalConfirmed(false);
      loadData();
    } catch (error) {
      // 409 = the invoice grand total we calculated doesn't match what the
      // supplier charged. Keep the modal open, show both figures, and let
      // the admin either fix the entry or accept the variance.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === "invoice_total_mismatch") {
        setInvoiceMismatch(detail);
        toast.error(detail.message || "Invoice total doesn't match");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to log delivery");
      }
    } finally {
      setIsSubmittingDelivery(false);
    }
  };

  // Create new product/item
  const handleCreateNewItem = async () => {
    if (!newItemData.name.trim() || !newItemData.category) {
      toast.error("Please enter item name and select a category");
      return;
    }

    setIsCreatingItem(true);
    try {
      // Calculate default quantity in base units
      let defaultQty = null;
      if (newItemData.default_initial_quantity) {
        defaultQty = parseFloat(newItemData.default_initial_quantity);
        // Convert meters to cm for Films/Wraps
        if (newItemData.category === "Films/Wraps") {
          defaultQty = defaultQty * 100;
        }
      }

      await axios.post(`${API}/stock/products`, {
        name: newItemData.name.trim(),
        category: newItemData.category,
        variant_type: newItemData.variant_type.trim() || null,
        shade_vlt: newItemData.shade_vlt.trim() || null,
        colour: newItemData.colour.trim() || null,
        size: newItemData.size.trim() || null,
        default_initial_quantity: defaultQty,
        // Data-entry convenience only — pre-fills the cost field when
        // adding a new stock unit. Server never uses this for margin
        // math; that always reads from the stock unit's own cost.
        default_cost: newItemData.default_cost
          ? parseFloat(newItemData.default_cost)
          : null,
        // "Minimum to keep" — stored in the product's base unit, so the
        // films input (meters) converts the same way as default quantity.
        low_stock_minimum: newItemData.low_stock_minimum
          ? parseFloat(newItemData.low_stock_minimum) * (newItemData.category === "Films/Wraps" ? 100 : 1)
          : null
      });

      toast.success(`Created new item: ${newItemData.name}`);
      setShowNewItemModal(false);
      setNewItemData({
        name: "",
        category: "",
        variant_type: "",
        shade_vlt: "",
        colour: "",
        size: "",
        default_initial_quantity: "",
        default_cost: ""
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create item");
    } finally {
      setIsCreatingItem(false);
    }
  };

  // Archive a product
  const handleArchiveProduct = async (product) => {
    setIsArchiving(true);
    try {
      await axios.delete(`${API}/stock/products/${product.product_id}`);
      toast.success(`Archived "${product.name}" - data preserved`);
      setArchivingProduct(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive item");
    } finally {
      setIsArchiving(false);
    }
  };

  // Restore an archived product
  const handleRestoreProduct = async (product) => {
    try {
      await axios.post(`${API}/stock/products/${product.product_id}/restore`);
      toast.success(`Restored "${product.name}"`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore item");
    }
  };

  // Open hard-delete confirmation. Fetches the unit count first so the
  // confirmation copy states exactly what will happen ("N unit(s) will
  // also be deleted") — no surprises after the admin clicks confirm.
  const openHardDeleteDialog = async (product) => {
    setHardDeleteTarget(product);
    setHardDeletePreview(null);
    try {
      const res = await axios.get(`${API}/stock/units?product_id=${product.product_id}&status=all`);
      setHardDeletePreview({ unit_count: (res.data || []).length });
    } catch (err) {
      // Non-fatal — the confirm still works; we just won't show a preview.
      console.error("Failed to fetch unit count for hard-delete preview:", err);
      setHardDeletePreview({ unit_count: null });
    }
  };

  // Confirm hard-delete. First tries without cascade; if backend refuses
  // with a unit-count 409, we re-issue with cascade=true (the admin has
  // already been told via the preview above that units will go too).
  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const needsCascade = (hardDeletePreview?.unit_count || 0) > 0;
    setIsHardDeleting(true);
    try {
      const url = `${API}/stock/products/${hardDeleteTarget.product_id}/hard-delete${needsCascade ? "?cascade=true" : ""}`;
      const res = await axios.delete(url);
      const unitsDel = res.data?.units_deleted || 0;
      toast.success(
        `Permanently deleted "${hardDeleteTarget.name}"` +
        (unitsDel > 0 ? ` (${unitsDel} unit${unitsDel === 1 ? "" : "s"} removed)` : "")
      );
      setHardDeleteTarget(null);
      setHardDeletePreview(null);
      loadData();
    } catch (error) {
      // Consumed-unit block or other guardrail — surface backend detail
      // verbatim so the admin knows exactly why.
      toast.error(error.response?.data?.detail || "Failed to permanently delete");
    } finally {
      setIsHardDeleting(false);
    }
  };

  // Open edit modal
  const openEditModal = (product) => {
    setEditingProduct(product);
    // Convert quantity back to display units
    let displayQty = "";
    if (product.default_initial_quantity) {
      if (product.base_unit === "cm") {
        displayQty = (product.default_initial_quantity / 100).toString();
      } else {
        displayQty = product.default_initial_quantity.toString();
      }
    }
    setEditData({
      name: product.name || "",
      category: product.category || "",
      variant_type: product.variant_type || "",
      shade_vlt: product.shade_vlt || "",
      colour: product.colour || "",
      size: product.size || "",
      default_initial_quantity: displayQty,
      default_cost:
        product.default_cost != null ? String(product.default_cost) : "",
      low_stock_minimum:
        product.low_stock_minimum != null
          ? String(product.base_unit === "cm" ? product.low_stock_minimum / 100 : product.low_stock_minimum)
          : ""
    });
  };

  // Save edited product
  const handleSaveEdit = async () => {
    if (!editData.name.trim()) {
      toast.error("Item name is required");
      return;
    }

    setIsEditing(true);
    try {
      // Calculate default quantity in base units
      let defaultQty = null;
      if (editData.default_initial_quantity) {
        defaultQty = parseFloat(editData.default_initial_quantity);
        // Convert meters to cm for Films/Wraps
        if (editData.category === "Films/Wraps") {
          defaultQty = defaultQty * 100;
        }
      }

      await axios.put(`${API}/stock/products/${editingProduct.product_id}`, {
        name: editData.name.trim(),
        category: editData.category || null,
        variant_type: editData.variant_type.trim() || null,
        shade_vlt: editData.shade_vlt.trim() || null,
        colour: editData.colour.trim() || null,
        size: editData.size.trim() || null,
        default_initial_quantity: defaultQty,
        // Data-entry convenience only. Server-side backend/routes/stock.py
        // uses `v is not None` filtering on PUT — empty string is ignored,
        // so the field can be edited independently of other fields.
        default_cost: editData.default_cost
          ? parseFloat(editData.default_cost)
          : null,
        // Explicit null clears the minimum (backend un-sets it and the
        // percentage rule takes over again).
        low_stock_minimum: editData.low_stock_minimum
          ? parseFloat(editData.low_stock_minimum) * (editData.category === "Films/Wraps" ? 100 : 1)
          : null
      });

      toast.success(`Updated "${editData.name}"`);
      setEditingProduct(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update item");
    } finally {
      setIsEditing(false);
    }
  };

  // Group archived products by category
  const archivedByCategory = archivedProducts.reduce((acc, product) => {
    const cat = product.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  // Filter products by search and category
  const getFilteredProducts = (categoryProducts) => {
    if (!categoryProducts) return [];
    
    return categoryProducts.filter(product => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!product.product_name.toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  };

  if (isLoading) {
    return (
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saturn-orange" />
        </CardContent>
      </Card>
    );
  }

  const categories = overview?.categories ? Object.keys(overview.categories) : [];
  const lowStockAlerts = overview?.low_stock_alerts || [];

  return (
    <>
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2 min-w-0">
              <Package className="w-5 h-5 text-saturn-orange flex-shrink-0" />
              <span className="truncate">Stock Inventory</span>
            </CardTitle>
            <div className="flex items-center flex-wrap gap-2 sm:gap-3">
              <div className="text-sm text-slate-400 whitespace-nowrap">
                {overview?.total_products || 0} products • {overview?.total_units || 0} units
              </div>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sendLowStockAlert}
                  disabled={isSendingAlert}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="check-stock-levels-btn"
                >
                  {isSendingAlert ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Mail className="w-3 h-3 mr-1" />
                  )}
                  Check & Alert
                </Button>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openBackfillModal(backfillIncludeDepleted)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 relative"
                  data-testid="backfill-costs-btn"
                  title="Retroactively price stock units that don't have a cost yet"
                >
                  <span className="mr-1">€</span>
                  Backfill Costs
                  {backfillMissingCount > 0 && (
                    <span
                      className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-300 text-xs font-semibold"
                      data-testid="backfill-missing-count"
                    >
                      {backfillMissingCount}
                    </span>
                  )}
                </Button>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openDeliveryModal}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="log-delivery-btn"
                >
                  <Package className="w-3 h-3 mr-1" />
                  Log Delivery
                </Button>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  onClick={() => setShowNewItemModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="add-new-item-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Item
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Low Stock Alerts */}
          {lowStockAlerts.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-red-400 font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  Low Stock Alert ({lowStockAlerts.length})
                </div>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20 h-7"
                    onClick={sendLowStockAlert}
                    disabled={isSendingAlert}
                    data-testid="send-low-stock-alert-btn"
                  >
                    {isSendingAlert ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Mail className="w-3 h-3 mr-1" />
                    )}
                    Email Alert
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                {lowStockAlerts.slice(0, 3).map((alert, idx) => (
                  <p key={alert.product_id || idx} className="text-sm text-red-300">
                    • {alert.product_name} — {alert.reason || "low stock"}
                  </p>
                ))}
                {lowStockAlerts.length > 3 && (
                  <p className="text-xs text-red-400">+{lowStockAlerts.length - 3} more...</p>
                )}
              </div>
            </div>
          )}

          {/* Search and Show Archived Toggle */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-slate-800 border-slate-700 text-white"
                data-testid="stock-search"
              />
            </div>
            {isAdmin && archivedProducts.length > 0 && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  data-testid="show-archived-toggle"
                />
                <Label className="text-sm text-slate-400 flex items-center gap-1 cursor-pointer" onClick={() => setShowArchived(!showArchived)}>
                  {showArchived ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  Archived ({archivedProducts.length})
                </Label>
              </div>
            )}
          </div>

          {/* Category Sections */}
          <div className="space-y-2">
            {categories.map(category => {
              const CategoryIcon = CATEGORY_ICONS[category] || Package;
              const categoryProducts = overview.categories[category] || [];
              const filteredProducts = getFilteredProducts(categoryProducts);
              const isExpanded = expandedCategories[category];
              
              // Calculate totals for this category
              const totalStatic = categoryProducts.reduce((sum, p) => sum + (p.static_full_units + p.static_partial_units), 0);
              const totalFloat = categoryProducts.reduce((sum, p) => sum + p.float_units.length, 0);

              return (
                <Collapsible
                  key={category}
                  open={isExpanded}
                  onOpenChange={() => toggleCategory(category)}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-800 transition-colors">
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <CategoryIcon className="w-5 h-5 text-saturn-orange" />
                        <span className="text-white font-medium">{category}</span>
                        <span className="text-xs text-slate-500">
                          ({categoryProducts.length} products)
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-400">
                          Static: <span className="text-white">{totalStatic}</span>
                        </span>
                        <span className="text-slate-400">
                          Float: <span className="text-saturn-orange">{totalFloat}</span>
                        </span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="mt-2 space-y-2 pl-4">
                      {filteredProducts.length === 0 ? (
                        <p className="text-slate-500 text-sm py-2">
                          {searchTerm ? "No matching products" : "No products in this category"}
                        </p>
                      ) : (
                        filteredProducts.map(product => (
                          <div
                            key={product.product_id}
                            className="bg-slate-800/30 rounded-lg p-3"
                          >
                            <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium break-words">{product.product_name}</p>
                                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                                  <span className="text-slate-400">
                                    Static: <span className="text-white break-words">{product.static_display}</span>
                                  </span>
                                  {product.float_display !== "None" && (
                                    <span className="text-slate-400">
                                      Float: <span className="text-saturn-orange break-words">{product.float_display}</span>
                                    </span>
                                  )}
                                </div>
                                {product.stock_status === "out" ? (
                                  <div
                                    className="flex items-center gap-1 mt-1 text-xs font-semibold text-red-500"
                                    data-testid={`out-of-stock-badge-${product.product_id}`}
                                  >
                                    <XCircle className="w-3 h-3" />
                                    Out of stock
                                  </div>
                                ) : product.is_low_stock ? (
                                  <div
                                    className="flex items-center gap-1 mt-1 text-xs text-amber-400"
                                    data-testid={`low-stock-badge-${product.product_id}`}
                                  >
                                    <AlertTriangle className="w-3 h-3" />
                                    {product.stock_label || "Low stock"}
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex items-center flex-wrap gap-2 flex-shrink-0">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const fullProduct = products.find(p => p.product_id === product.product_id);
                                    if (fullProduct) {
                                      openAddModal(fullProduct);
                                    }
                                  }}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`add-stock-${product.product_id}`}
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  Add Stock
                                </Button>
                                {isAdmin && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        const fullProduct = products.find(p => p.product_id === product.product_id);
                                        if (fullProduct) {
                                          setAdjustingProduct(fullProduct);
                                        }
                                      }}
                                      className="text-slate-400 hover:text-saturn-orange hover:bg-orange-500/10"
                                      title="Adjust stock level on individual units"
                                      data-testid={`adjust-level-${product.product_id}`}
                                    >
                                      <Sliders className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        const fullProduct = products.find(p => p.product_id === product.product_id);
                                        if (fullProduct) {
                                          openEditModal(fullProduct);
                                        }
                                      }}
                                      className="text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
                                      data-testid={`edit-${product.product_id}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        const fullProduct = products.find(p => p.product_id === product.product_id);
                                        if (fullProduct) {
                                          setArchivingProduct(fullProduct);
                                        }
                                      }}
                                      className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                                      data-testid={`archive-${product.product_id}`}
                                    >
                                      <Archive className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>

          {/* Archived Products Section */}
          {showArchived && archivedProducts.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-700">
              <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                <Archive className="w-4 h-4" />
                Archived Items ({archivedProducts.length})
              </h3>
              <div className="space-y-2">
                {Object.entries(archivedByCategory).map(([category, categoryProducts]) => (
                  <div key={category} className="space-y-2">
                    <p className="text-xs text-slate-500 font-medium">{category}</p>
                    {categoryProducts.map(product => (
                      <div
                        key={product.product_id}
                        className="flex items-center justify-between bg-slate-800/20 rounded-lg p-3 opacity-60"
                      >
                        <div>
                          <p className="text-slate-400">{product.name}</p>
                          <p className="text-xs text-slate-500">
                            Archived {product.archived_at ? new Date(product.archived_at).toLocaleDateString() : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestoreProduct(product)}
                            className="border-slate-600 text-slate-300 hover:bg-green-500/20 hover:text-green-400 hover:border-green-500"
                            data-testid={`restore-${product.product_id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openHardDeleteDialog(product)}
                            className="border-red-500/50 text-red-300 hover:bg-red-500/20 hover:text-red-200 hover:border-red-500"
                            title="Permanently remove this archived product"
                            data-testid={`hard-delete-${product.product_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Permanently
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Supplier-name cleanup (admin) — rename/merge free-text vendor names */}
      {isAdmin && <SupplierNamesCard />}

      {/* Archive Confirmation Dialog */}
      <Dialog open={!!archivingProduct} onOpenChange={(open) => !open && setArchivingProduct(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500">
              <Archive className="w-5 h-5" />
              Archive Item?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to archive <span className="text-white font-medium">{archivingProduct?.name}</span>?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
              <p className="text-slate-300 mb-2">This will:</p>
              <ul className="text-slate-400 space-y-1">
                <li>• Hide this item from active inventory</li>
                <li>• Preserve all historical data & job records</li>
                <li>• Allow restoration at any time</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setArchivingProduct(null)} 
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleArchiveProduct(archivingProduct)}
              disabled={isArchiving}
              className="bg-yellow-600 hover:bg-yellow-700"
              data-testid="confirm-archive"
            >
              {isArchiving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-1" />
                  Archive
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stock Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-500" />
              Add Stock
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {addingToProduct?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Quantity Count */}
            <div className="space-y-2">
              <Label className="text-white">
                Number of {addingToProduct?.container_type || "unit"}s to add
              </Label>
              <Input
                type="number"
                min="1"
                value={addQuantityCount}
                onChange={(e) => setAddQuantityCount(parseInt(e.target.value) || 1)}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="add-quantity-count"
              />
              <p className="text-slate-500 text-xs">
                e.g., &quot;Add 3 rolls&quot; creates 3 separate trackable units
              </p>
            </div>

            {/* Initial Quantity per Unit */}
            <div className="space-y-2">
              <Label className="text-white">
                {addingToProduct?.base_unit === "cm" ? "Meters per roll" :
                 addingToProduct?.base_unit === "ml" ? "ml per bottle" :
                 "Pieces per unit"}
              </Label>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                value={addInitialQuantity}
                onChange={(e) => setAddInitialQuantity(e.target.value)}
                placeholder={addingToProduct?.base_unit === "cm" ? "e.g., 30 (for 30m roll)" : "e.g., 500"}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="add-initial-quantity"
              />
            </div>

            {/* --- Delivery / cost metadata (all optional, admin-only) --- */}
            {isAdmin && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-white text-sm">
                      Cost per {addingToProduct?.container_type || "unit"} (€)
                      <span className="text-slate-500 font-normal"> — optional</span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={addCost}
                      onChange={(e) => setAddCost(e.target.value)}
                      placeholder="e.g. 200"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="add-cost"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Purchase date</Label>
                    <Input
                      type="date"
                      value={addPurchaseDate}
                      onChange={(e) => setAddPurchaseDate(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="add-purchase-date"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white text-sm">
                    Vendor / Supplier
                    <span className="text-slate-500 font-normal"> — optional</span>
                  </Label>
                  <SuggestInput
                    value={addVendor}
                    onChange={setAddVendor}
                    suggestions={suggestions.vendors}
                    placeholder="e.g. 3M Ireland"
                    className="bg-slate-800 border-slate-700 text-white"
                    testId="add-vendor-name"
                  />
                </div>

                {/* VAT treatment + net/gross toggle — mirrors the Backfill
                    modal so new units are classified at intake time, not
                    left dangling as "unclassified" for later cleanup. Only
                    surfaced when a cost has been entered. */}
                {addCost.trim() !== "" && (
                  <div className="rounded-md bg-slate-800/40 border border-slate-700 p-3 space-y-2">
                    <Label className="text-white text-sm">VAT treatment</Label>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={addVatTreatment}
                          onChange={(e) => setAddVatTreatment(e.target.value)}
                          className="bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1 h-8"
                          data-testid="add-vat-treatment"
                        >
                          <option value="standard">Standard (Irish supplier)</option>
                          <option value="eu_reverse_charge">EU reverse charge</option>
                          <option value="no_vat">No VAT (non-EU / exempt)</option>
                        </select>
                      </div>
                      {addVatTreatment === "standard" && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-400">Amount is:</span>
                          <div className="inline-flex rounded overflow-hidden border border-slate-700">
                            <button
                              type="button"
                              onClick={() => setAddCostMode("net")}
                              className={`px-2 py-1 text-xs ${addCostMode === "net" ? "bg-saturn-orange text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}
                              data-testid="add-cost-mode-net"
                            >
                              Net (ex-VAT)
                            </button>
                            <button
                              type="button"
                              onClick={() => setAddCostMode("gross")}
                              className={`px-2 py-1 text-xs ${addCostMode === "gross" ? "bg-saturn-orange text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}
                              data-testid="add-cost-mode-gross"
                            >
                              Gross (VAT-inc)
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {addVatTreatment === "eu_reverse_charge" && (
                      <p className="text-amber-400/80 text-[11px]">
                        Net paid (no supplier VAT). Self-accounted VAT recorded separately at 23%.
                      </p>
                    )}
                    {addVatTreatment === "no_vat" && (
                      <p className="text-slate-500 text-[11px]">
                        No VAT involved (non-EU import or genuinely exempt).
                      </p>
                    )}
                    {/* Cost sanity check — only meaningful for standard-rate
                        Irish supplier purchases (EU RC / no_vat entries
                        legitimately record the full paid figure as net, so
                        comparing them against a Net-mode baseline would
                        produce false positives). */}
                    {(() => {
                      if (addVatTreatment !== "standard") return null;
                      const avg = addRecentCosts?.average_net_cost;
                      const sampleCount = addRecentCosts?.count || 0;
                      if (!avg || sampleCount < 1) return null;
                      const typed = parseFloat(addCost);
                      if (isNaN(typed) || typed <= 0) return null;

                      // Compute the "effective net" that would land in the
                      // DB with the CURRENT toggle setting, so the check
                      // is honest about which mode we're in.
                      const effectiveNet =
                        addCostMode === "gross" ? typed / 1.23 : typed;
                      const drift = Math.abs(effectiveNet - avg) / avg;
                      if (drift < 0.3) return null;

                      // If the toggle is on Net but the value looks
                      // suspiciously like Gross (≈typed / 1.23 lines up
                      // with the baseline), pitch the swap. Otherwise
                      // it's just a genuine price change — flag it
                      // gently without offering the auto-swap.
                      const grossAsNet = typed / 1.23;
                      const grossFits = Math.abs(grossAsNet - avg) / avg < 0.1;
                      const overOrUnder =
                        effectiveNet > avg ? "higher" : "lower";
                      const driftPct = Math.round(drift * 100);
                      return (
                        <div
                          className="mt-1 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200 space-y-1"
                          data-testid="add-cost-sanity-nudge"
                        >
                          <div>
                            <strong>Heads up:</strong> €{typed.toFixed(2)}{" "}
                            ({addCostMode}) sits {driftPct}% {overOrUnder}{" "}
                            than the last {sampleCount} net purchase
                            {sampleCount === 1 ? "" : "s"} of{" "}
                            {addingToProduct?.name} (avg €{avg.toFixed(2)}).
                          </div>
                          {grossFits && addCostMode === "net" && (
                            <div className="flex items-center gap-2">
                              <span className="text-amber-200/90">
                                Was this actually the Gross (VAT-inc) price?
                              </span>
                              <button
                                type="button"
                                onClick={() => setAddCostMode("gross")}
                                className="px-2 py-0.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded text-[11px] font-medium"
                                data-testid="add-cost-sanity-swap-btn"
                              >
                                Yes, switch to Gross
                              </button>
                            </div>
                          )}
                          {grossFits && addCostMode === "gross" && effectiveNet < avg && (
                            <div className="text-amber-200/80">
                              Kept in Gross mode — server will record
                              €{effectiveNet.toFixed(2)} net and €
                              {(typed - effectiveNet).toFixed(2)} VAT.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            )}

            {/* Preview */}
            {addQuantityCount > 0 && addInitialQuantity && (
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm text-slate-400">Preview:</p>
                <p className="text-white">
                  Adding <span className="text-saturn-orange font-bold">{addQuantityCount}</span> {addingToProduct?.container_type}(s) 
                  of <span className="text-saturn-orange font-bold">
                    {addingToProduct?.base_unit === "cm" 
                      ? `${addInitialQuantity}m` 
                      : addingToProduct?.base_unit === "ml"
                        ? `${addInitialQuantity}ml`
                        : `${addInitialQuantity} pcs`}
                  </span> each
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleAddStock}
              disabled={isAdding || !addInitialQuantity}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-add-stock"
            >
              {isAdding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1" />
                  Add to Inventory
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Item Modal */}
      <Dialog open={showNewItemModal} onOpenChange={setShowNewItemModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-500" />
              Add New Item
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new product definition for your inventory
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Item Name */}
            <div className="space-y-2">
              <Label className="text-white">Item Name <span className="text-red-400">*</span></Label>
              <Input
                value={newItemData.name}
                onChange={(e) => setNewItemData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Ceramic Tint Film 35% VLT - 100cm"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="new-item-name"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-white">Category <span className="text-red-400">*</span></Label>
              <Select 
                value={newItemData.category} 
                onValueChange={(val) => setNewItemData(prev => ({
                  ...prev,
                  category: val,
                  ...clearedHiddenFields(val),
                }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="new-item-category">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional Fields — only the attributes that apply to the
                selected category (see CATEGORY_OPTIONAL_FIELDS). */}
            <OptionalFieldsGrid
              category={newItemData.category}
              data={newItemData}
              onFieldChange={(key, value) => setNewItemData(prev => ({ ...prev, [key]: value }))}
              testIdPrefix="new-item"
              suggestions={suggestions}
            />

            {/* Default Quantity */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">
                Default Quantity per Unit 
                <span className="text-xs ml-1">
                  ({newItemData.category === "Films/Wraps" ? "meters" : 
                    newItemData.category === "Chemicals/Liquids" ? "ml" : "pieces"})
                </span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={newItemData.default_initial_quantity}
                onChange={(e) => setNewItemData(prev => ({ ...prev, default_initial_quantity: e.target.value }))}
                placeholder={newItemData.category === "Films/Wraps" ? "e.g., 30 (for 30m roll)" : "e.g., 500"}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500">
                Pre-fills when adding stock. Leave empty if quantity varies.
              </p>
            </div>

            {/* Default Cost — data-entry convenience only. Never
                touches material-cost / margin math (that always reads
                each stock unit's own recorded cost). Applies per-unit,
                same scoping as Default Quantity above. */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">
                Default Cost per Unit
                <span className="text-xs ml-1">(€, ex-VAT)</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={newItemData.default_cost}
                onChange={(e) => setNewItemData(prev => ({ ...prev, default_cost: e.target.value }))}
                placeholder="e.g., 200 (for a €200 roll)"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="new-item-default-cost"
              />
              <p className="text-xs text-slate-500">
                Pre-fills the cost field when a delivery adds a new unit for
                this product. Staff can still override per delivery. Never
                used directly in margin/profitability math.
              </p>
            </div>

            {/* Minimum to keep — overrides the percentage rule. Needed for
                count-based items (squeegees, gloves, blades) stored as
                units of 1 piece, which a percentage can never flag. */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">
                Minimum to Keep
                <span className="text-xs ml-1">
                  (optional — {newItemData.category === "Films/Wraps" ? "meters" :
                    newItemData.category === "Chemicals/Liquids" ? "ml" : "pieces"})
                </span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={newItemData.low_stock_minimum}
                onChange={(e) => setNewItemData(prev => ({ ...prev, low_stock_minimum: e.target.value }))}
                placeholder={newItemData.category === "Films/Wraps" ? "e.g., 5 (for 5m)" : "e.g., 2"}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="new-item-low-stock-minimum"
              />
              <p className="text-xs text-slate-500">
                Alert when fewer than this many are left, counting the shelf
                and float together.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowNewItemModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewItem}
              disabled={isCreatingItem || !newItemData.name.trim() || !newItemData.category}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-new-item"
            >
              {isCreatingItem ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1" />
                  Create Item
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Modal */}
      <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit Item
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update product details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Item Name */}
            <div className="space-y-2">
              <Label className="text-white">Item Name <span className="text-red-400">*</span></Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Ceramic Tint Film 35% VLT - 100cm"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="edit-item-name"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-white">Category</Label>
              <Select 
                value={editData.category} 
                onValueChange={(val) => setEditData(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-item-category">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional Fields — category-aware, but any field this
                product already has a value in stays visible so saved
                data is never silently hidden. */}
            <OptionalFieldsGrid
              category={editData.category}
              data={editData}
              onFieldChange={(key, value) => setEditData(prev => ({ ...prev, [key]: value }))}
              testIdPrefix="edit-item"
              suggestions={suggestions}
            />

            {/* Default Quantity */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">
                Default Quantity per Unit 
                <span className="text-xs ml-1">
                  ({editData.category === "Films/Wraps" ? "meters" : 
                    editData.category === "Chemicals/Liquids" ? "ml" : "pieces"})
                </span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={editData.default_initial_quantity}
                onChange={(e) => setEditData(prev => ({ ...prev, default_initial_quantity: e.target.value }))}
                placeholder={editData.category === "Films/Wraps" ? "e.g., 30 (for 30m roll)" : "e.g., 500"}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            {/* Default Cost — see New Item modal for full rationale. */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">
                Default Cost per Unit
                <span className="text-xs ml-1">(€, ex-VAT)</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editData.default_cost}
                onChange={(e) => setEditData(prev => ({ ...prev, default_cost: e.target.value }))}
                placeholder="e.g., 200 (for a €200 roll)"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="edit-item-default-cost"
              />
              <p className="text-xs text-slate-500">
                Pre-fills the cost field when a delivery adds a new unit.
                Never used directly in margin math — existing units keep
                their originally-recorded cost.
              </p>
            </div>

            {/* Minimum to keep — see New Item modal for rationale. */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">
                Minimum to Keep
                <span className="text-xs ml-1">
                  (optional — {editData.category === "Films/Wraps" ? "meters" :
                    editData.category === "Chemicals/Liquids" ? "ml" : "pieces"})
                </span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={editData.low_stock_minimum}
                onChange={(e) => setEditData(prev => ({ ...prev, low_stock_minimum: e.target.value }))}
                placeholder={editData.category === "Films/Wraps" ? "e.g., 5 (for 5m)" : "e.g., 2"}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="edit-item-low-stock-minimum"
              />
              <p className="text-xs text-slate-500">
                Alert when fewer than this many are left, counting the shelf
                and float together. Leave empty to use the percentage rule.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEditingProduct(null)} 
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isEditing || !editData.name.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-edit-item"
            >
              {isEditing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Pencil className="w-4 h-4 mr-1" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delivery Modal — multi-product intake in one round-trip */}
      <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="delivery-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Log Supplier Delivery
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Log a whole delivery in one go — one vendor + date, multiple products.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Vendor + Date (shared for the whole delivery) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-white text-sm">Vendor / Supplier</Label>
                <SuggestInput
                  value={deliveryVendor}
                  onChange={setDeliveryVendor}
                  suggestions={suggestions.vendors}
                  placeholder="e.g. 3M Ireland"
                  className="bg-slate-800 border-slate-700 text-white"
                  testId="delivery-vendor"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white text-sm">Purchase date</Label>
                <Input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="delivery-purchase-date"
                />
              </div>
            </div>

            {/* VAT treatment + net/gross toggle — shared across every line
                in the delivery (one supplier invoice = one treatment). Only
                lines with a cost pick this up; blank-cost lines stay
                un-classified and un-costed, same as before. */}
            <div className="rounded-md bg-slate-800/40 border border-slate-700 p-3 space-y-2">
              <Label className="text-white text-sm">VAT treatment (whole delivery)</Label>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <select
                    value={deliveryVatTreatment}
                    onChange={(e) => setDeliveryVatTreatment(e.target.value)}
                    className="bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1 h-8"
                    data-testid="delivery-vat-treatment"
                  >
                    <option value="standard">Standard (Irish supplier)</option>
                    <option value="eu_reverse_charge">EU reverse charge</option>
                    <option value="no_vat">No VAT (non-EU / exempt)</option>
                  </select>
                </div>
                {deliveryVatTreatment === "standard" && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400">Line costs are:</span>
                    <div className="inline-flex rounded overflow-hidden border border-slate-700">
                      <button
                        type="button"
                        onClick={() => setDeliveryCostMode("net")}
                        className={`px-2 py-1 text-xs ${deliveryCostMode === "net" ? "bg-saturn-orange text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}
                        data-testid="delivery-cost-mode-net"
                      >
                        Net (ex-VAT)
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryCostMode("gross")}
                        className={`px-2 py-1 text-xs ${deliveryCostMode === "gross" ? "bg-saturn-orange text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}
                        data-testid="delivery-cost-mode-gross"
                      >
                        Gross (VAT-inc)
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {deliveryVatTreatment === "eu_reverse_charge" && (
                <p className="text-amber-400/80 text-[11px]">
                  Net paid (no supplier VAT). Self-accounted VAT recorded separately at 23%.
                </p>
              )}
              {deliveryVatTreatment === "no_vat" && (
                <p className="text-slate-500 text-[11px]">
                  No VAT involved (non-EU import or genuinely exempt).
                </p>
              )}
            </div>

            {/* Shipping / delivery charge — one total for the whole
                delivery. The server spreads it pro-rata across the priced
                lines by line value and folds each share into that unit's
                stored cost, so job material-cost and margin figures use
                true landed cost without any extra bookkeeping. */}
            <div className="rounded-md bg-slate-800/40 border border-slate-700 p-3 space-y-2" data-testid="delivery-shipping-block">
              <Label className="text-white text-sm">
                Shipping / delivery charge
                <span className="text-slate-500 font-normal"> — optional, whole delivery</span>
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryShipping}
                  onChange={(e) => setDeliveryShipping(e.target.value)}
                  placeholder={
                    deliveryVatTreatment === "standard" && deliveryCostMode === "gross"
                      ? "€ total (gross)"
                      : "€ total (net)"
                  }
                  className="bg-slate-900 border-slate-700 text-white h-9 max-w-[180px]"
                  data-testid="delivery-shipping-cost"
                />
                <p className="text-slate-500 text-[11px]">
                  Spread across priced lines by line value — no need to fold it into
                  per-unit cost yourself any more.
                </p>
              </div>

              {/* Invoice grand-total check — optional guardrail. Typing the
                  supplier's printed total lets the server verify the cost
                  entry adds up before anything is saved. This is the check
                  that would have caught €28.65/m being typed into a
                  €655.00 roll-cost field. */}
              <div className="pt-2 border-t border-slate-700/70">
                <Label className="text-white text-sm">
                  Invoice grand total
                  <span className="text-slate-500 font-normal"> — optional, what you actually paid</span>
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryInvoiceTotal}
                    onChange={(e) => {
                      setDeliveryInvoiceTotal(e.target.value);
                      setInvoiceMismatch(null);
                      setInvoiceTotalConfirmed(false);
                    }}
                    placeholder="€ incl. shipping + VAT"
                    className="bg-slate-900 border-slate-700 text-white h-9 max-w-[180px]"
                    data-testid="delivery-invoice-total"
                  />
                  <p className="text-slate-500 text-[11px]">
                    Checked against goods + shipping + VAT when you log the delivery.
                    Leave blank if you don&apos;t have the invoice to hand.
                  </p>
                </div>
              </div>
            </div>

            {/* Reconciliation failure — server-calculated vs invoice, side
                by side, with an explicit accept-the-variance tick. Never a
                hard block: the admin may have a legitimate reason (credit
                note, part-delivery). */}
            {invoiceMismatch && (
              <div
                className="rounded-md border border-red-500/50 bg-red-500/10 p-3 space-y-3"
                data-testid="invoice-total-mismatch"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-red-200 text-xs font-semibold">
                    Invoice total doesn&apos;t reconcile — check the costs before saving.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs pl-6">
                  <div className="rounded bg-slate-900/60 p-2">
                    <div className="text-slate-400 text-[11px]">You entered (invoice)</div>
                    <div className="text-white font-mono text-base" data-testid="invoice-total-entered">
                      €{Number(invoiceMismatch.invoice_total_entered).toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded bg-slate-900/60 p-2">
                    <div className="text-slate-400 text-[11px]">Calculated from your entry</div>
                    <div className="text-white font-mono text-base" data-testid="invoice-total-expected">
                      €{Number(invoiceMismatch.expected_total).toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="pl-6 text-[11px] text-slate-400 space-y-0.5">
                  <div>
                    Goods €{Number(invoiceMismatch.breakdown?.goods_net || 0).toFixed(2)}
                    {" + "}shipping €{Number(invoiceMismatch.breakdown?.shipping_net || 0).toFixed(2)}
                    {" + "}VAT €{Number(invoiceMismatch.breakdown?.vat_payable || 0).toFixed(2)}
                  </div>
                  <div className="text-red-300 font-semibold" data-testid="invoice-total-difference">
                    Out by €{Number(invoiceMismatch.difference).toFixed(2)}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-red-200 cursor-pointer pl-6">
                  <input
                    type="checkbox"
                    checked={invoiceTotalConfirmed}
                    onChange={(e) => setInvoiceTotalConfirmed(e.target.checked)}
                    className="accent-red-500"
                    data-testid="invoice-total-confirm"
                  />
                  The entry is correct — log it anyway with this variance
                </label>
              </div>
            )}

            {/* Receipts to attach at intake — the "snap the supplier
                invoice as stock lands" flow. Files stay client-side
                until POST /deliveries succeeds, then each uploads
                against the returned delivery_id. Images + PDFs, ≤10 MB
                each, same server rules as the Purchases tab uploader. */}
            <div className="rounded-md bg-slate-800/40 border border-slate-700 p-3 space-y-2" data-testid="delivery-receipts-block">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-white text-sm">
                  Supplier receipt / invoice
                  <span className="text-slate-500 font-normal"> — optional</span>
                </Label>
                <div className="flex items-center gap-2">
                  {/* Camera capture — mobile browsers open the rear camera
                      directly (accept=image/* + capture="environment"). On
                      desktop the attribute is ignored and this behaves
                      like a plain image file picker. */}
                  <label
                    className="cursor-pointer text-xs text-saturn-orange hover:text-orange-300 border border-saturn-orange/50 hover:border-orange-300 rounded px-2 py-1 flex items-center gap-1"
                    data-testid="delivery-receipt-camera-btn"
                    title="Snap the paper receipt with your camera"
                  >
                    <Camera className="w-3.5 h-3.5" /> Take photo
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        e.target.value = "";
                        const kept = [];
                        for (const f of picked) {
                          if (f.size > 10 * 1024 * 1024) {
                            toast.error(`${f.name} is over 10 MB — skipped`);
                            continue;
                          }
                          kept.push(f);
                        }
                        if (kept.length) {
                          setDeliveryReceipts((prev) => [...prev, ...kept]);
                        }
                      }}
                    />
                  </label>
                  <label
                    className="cursor-pointer text-xs text-saturn-orange hover:text-orange-300 border border-saturn-orange/50 hover:border-orange-300 rounded px-2 py-1"
                    data-testid="delivery-receipt-add-btn"
                    title="Attach a saved image or PDF"
                  >
                    + Add file
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
                      className="hidden"
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        e.target.value = "";
                        const kept = [];
                        for (const f of picked) {
                          if (f.size > 10 * 1024 * 1024) {
                            toast.error(`${f.name} is over 10 MB — skipped`);
                            continue;
                          }
                          kept.push(f);
                        }
                        if (kept.length) {
                          setDeliveryReceipts((prev) => [...prev, ...kept]);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
              {deliveryReceipts.length === 0 ? (
                <p className="text-slate-500 text-[11px]">
                  Attach the supplier invoice, delivery note or photo of the paper receipt now — image or PDF, up to 10 MB each. You can also add these later from the Purchases tab.
                </p>
              ) : (
                <div className="space-y-1">
                  {deliveryReceipts.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between gap-2 bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5 text-xs"
                      data-testid={`delivery-receipt-staged-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-slate-300 truncate">{f.name}</span>
                        <span className="text-slate-500 flex-shrink-0">
                          ({(f.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setDeliveryReceipts((prev) =>
                            prev.filter((_, idx) => idx !== i)
                          )
                        }
                        className="text-rose-400 hover:text-rose-300 flex-shrink-0"
                        data-testid={`delivery-receipt-remove-${i}`}
                        aria-label={`Remove ${f.name}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product lines */}
            <div className="space-y-2">
              <Label className="text-white text-sm">Products in this delivery</Label>
              {deliveryLines.map((line, idx) => {
                const prod = products.find((p) => p.product_id === line.product_id);
                const qtyLabel = prod?.base_unit === "cm" ? "meters/roll"
                  : prod?.base_unit === "ml" ? "ml/bottle"
                    : "pcs/unit";
                // Live per-unit-of-measure cost preview — computed as the
                // admin types both cost + quantity. Kills the ambiguity
                // that led to the Aug 2026 Chameleon-film bug: typing
                // "30" for a 3m roll that actually cost €90 total now
                // renders "= €10.00 per meter" immediately, making it
                // obvious the wrong number was entered. Green when both
                // fields are valid; hidden otherwise (no noise while
                // typing partial values).
                const cNum = parseFloat(line.cost);
                const qNum = parseFloat(line.initial_quantity);
                const perUomLabel = prod?.base_unit === "cm" ? "meter"
                  : prod?.base_unit === "ml" ? "ml"
                    : "unit";
                const preview =
                  !isNaN(cNum) && !isNaN(qNum) && qNum > 0 && cNum > 0
                    ? { perUom: cNum / qNum, qty: qNum }
                    : null;
                return (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-start bg-slate-800/40 rounded-md p-3"
                    data-testid={`delivery-line-${idx}`}
                  >
                    <div className="col-span-5">
                      <Select
                        value={line.product_id}
                        onValueChange={(v) => updateDeliveryLine(idx, "product_id", v)}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm">
                          <SelectValue placeholder="Choose product…" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-72">
                          {products.map((p) => (
                            <SelectItem key={p.product_id} value={p.product_id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={line.quantity_count}
                      onChange={(e) => updateDeliveryLine(idx, "quantity_count", e.target.value)}
                      placeholder="Count"
                      className="col-span-2 bg-slate-800 border-slate-700 text-white text-sm"
                      title="How many of this item?"
                    />
                    <Input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={line.initial_quantity}
                      onChange={(e) => updateDeliveryLine(idx, "initial_quantity", e.target.value)}
                      placeholder={qtyLabel}
                      className="col-span-2 bg-slate-800 border-slate-700 text-white text-sm"
                      title={`Amount per unit (${qtyLabel})`}
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.cost}
                      onChange={(e) => updateDeliveryLine(idx, "cost", e.target.value)}
                      placeholder={deliveryVatTreatment === "standard" && deliveryCostMode === "gross" ? "€ each (gross)" : "€ each (net)"}
                      className="col-span-2 bg-slate-800 border-slate-700 text-white text-sm"
                      title={`Cost per unit (${deliveryVatTreatment === "standard" && deliveryCostMode === "gross" ? "VAT-inclusive" : "net, ex-VAT"}) — optional`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDeliveryLine(idx)}
                      disabled={deliveryLines.length === 1}
                      className="col-span-1 text-slate-400 hover:text-red-400 h-9"
                      title="Remove line"
                    >
                      ×
                    </Button>
                    {preview && (
                      <p
                        className="col-span-12 text-emerald-400 text-xs font-mono pl-1"
                        data-testid={`delivery-line-preview-${idx}`}
                      >
                        = €{preview.perUom.toFixed(2)} per {perUomLabel} (over {preview.qty} {perUomLabel}{preview.qty === 1 ? "" : "s"})
                      </p>
                    )}
                    {/* Sanity check — hides itself when this product
                        has <3 prior costed deliveries (backend
                        returns `insufficient: true`). Prevents false
                        positives on brand-new products. */}
                    {preview && line.product_id && costBaselines[line.product_id] && (
                      <div className="col-span-12" data-testid={`delivery-line-sanity-${idx}`}>
                        <CostSanityWarning
                          baseline={costBaselines[line.product_id]}
                          currentPerUom={preview.perUom}
                          onOverride={() => setSanityOverrides((prev) => ({ ...prev, [idx]: true }))}
                          overridden={!!sanityOverrides[idx]}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={addDeliveryLine}
                className="w-full border-dashed border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="add-delivery-line-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add another product line
              </Button>
              <p className="text-slate-500 text-xs">
                <span className="text-slate-300 font-medium">Cost is the total for one whole unit</span>{" "}
                (one roll, one bottle, one piece) — <span className="text-slate-300">not per meter or per ml</span>.
                Watch the green preview to confirm the per-meter / per-ml figure looks right before saving.
                Leave blank if you don&apos;t track cost for a product — it just won&apos;t contribute to job material-cost reports.
              </p>
            </div>
          </div>

          {/* Missing-cost guard — an uncosted unit makes every job that
              consumes it look free, understating cost and overstating
              margin forever. Submitting anyway requires an explicit tick. */}
          {(() => {
            const uncosted = deliveryLines.filter((l) => {
              if (!l.product_id || !l.initial_quantity) return false;
              const c = parseFloat(l.cost);
              return l.cost === "" || isNaN(c) || c <= 0;
            });
            if (uncosted.length === 0) return null;
            return (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
                data-testid="delivery-missing-cost-warning"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-amber-200 text-xs">
                    <span className="font-semibold">
                      {uncosted.length} line{uncosted.length === 1 ? "" : "s"} has no cost.
                    </span>{" "}
                    Stock logged without a cost contributes €0 to every job that uses it —
                    job costs read low and margins read high, permanently.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-amber-200 cursor-pointer pl-6">
                  <input
                    type="checkbox"
                    checked={allowMissingCost}
                    onChange={(e) => setAllowMissingCost(e.target.checked)}
                    className="accent-amber-500"
                    data-testid="delivery-allow-missing-cost"
                  />
                  Log without cost anyway — I&apos;ll backfill it later
                </label>
              </div>
            );
          })()}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeliveryModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitDelivery}
              disabled={isSubmittingDelivery}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-delivery-btn"
            >
              {isSubmittingDelivery ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Package className="w-4 h-4 mr-1" /> Log Delivery
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost Backfill Modal — one row per uncosted unit with inline save */}
      <Dialog open={showBackfillModal} onOpenChange={setShowBackfillModal}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto"
          data-testid="backfill-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-emerald-400 text-xl">€</span>
              Backfill Stock Costs
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Retroactively price stock added before you started tracking cost.
              Each saved row starts contributing to job material-cost analytics immediately.
            </DialogDescription>
          </DialogHeader>

          {/* Scope toggle — depleted units are hidden by default (you can't
              re-price a bottle you binned for future jobs), but including
              them shows the full historical blast radius: every job that
              consumed uncosted stock is under-reporting its cost. */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-700 bg-slate-800/40 px-3 py-2"
            data-testid="backfill-scope-bar"
          >
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={backfillIncludeDepleted}
                onChange={toggleBackfillDepleted}
                className="accent-saturn-orange"
                data-testid="backfill-include-depleted"
              />
              Include used-up (depleted) units — full historical scope
            </label>
            {backfillStats && (
              <div className="text-xs text-slate-400" data-testid="backfill-scope-stats">
                <span className="text-white font-semibold">{backfillStats.count || 0}</span> uncosted
                {" · "}
                {backfillStats.active_count || 0} active
                {backfillIncludeDepleted ? ` · ${backfillStats.depleted_count || 0} depleted` : ""}
                {" · "}
                <span className={backfillStats.jobs_affected_total ? "text-amber-400" : ""}>
                  {backfillStats.jobs_affected_total || 0} job entries affected
                </span>
              </div>
            )}
          </div>

          <div className="py-4">
            {backfillLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading uncosted units…
              </div>
            ) : backfillUnits.length === 0 ? (
              <div
                className="text-center py-10 text-slate-400"
                data-testid="backfill-empty"
              >
                <div className="text-4xl mb-2">✓</div>
                <div className="text-white font-medium">All active units have cost data</div>
                <div className="text-sm text-slate-500 mt-1">Nothing to backfill right now.</div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="backfill-units-list">
                <div className="text-xs text-slate-500 mb-2">
                  {backfillUnits.length} unit{backfillUnits.length === 1 ? "" : "s"} without cost
                  {" — "}grouped by product. Use the group bar for the same price across all;
                  edit any row individually if a batch had a different price.
                </div>
                {(() => {
                  // Group by product_name preserving newest-first ordering
                  const groups = [];
                  const seen = new Map();
                  for (const u of backfillUnits) {
                    if (!seen.has(u.product_name)) {
                      seen.set(u.product_name, groups.length);
                      groups.push({ product_name: u.product_name, units: [] });
                    }
                    groups[seen.get(u.product_name)].units.push(u);
                  }
                  return groups.map(({ product_name, units }) => {
                    const groupDraft = bulkGroupDrafts[product_name] || {};
                    const treatment = groupDraft.vat_treatment || "standard";
                    const inputMode = groupDraft.input_mode || "net"; // "net" | "gross"
                    const displaySample = units[0];
                    const displayQty = displaySample?.base_unit === "cm"
                      ? `${(displaySample.initial_quantity / 100).toFixed(1)}m`
                      : `${displaySample?.initial_quantity ?? ""}${displaySample?.base_unit || ""}`;
                    const isSavingGroup = bulkGroupSaving === product_name;
                    return (
                      <div
                        key={product_name}
                        className="rounded-lg border border-slate-700 bg-slate-800/30 overflow-hidden"
                        data-testid={`backfill-group-${product_name}`}
                      >
                        {/* Group bulk-apply header */}
                        <div
                          className="bg-slate-800 px-3 py-2.5 space-y-2"
                          data-testid={`backfill-group-header-${product_name}`}
                        >
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-4 min-w-0">
                              <div className="text-white text-sm font-semibold truncate">
                                {product_name}
                              </div>
                              <div className="text-xs text-slate-500">
                                {units.length} uncosted unit{units.length === 1 ? "" : "s"}
                                {displaySample?.base_unit ? ` · pack ${displayQty}` : ""}
                              </div>
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number" min="0" step="0.01"
                                value={groupDraft.cost || ""}
                                onChange={(e) => updateBulkGroupDraft(product_name, "cost", e.target.value)}
                                placeholder={inputMode === "gross" ? "€ gross (VAT-inc)" : "€ net (ex-VAT)"}
                                className="bg-slate-900 border-slate-700 text-white text-sm h-9"
                                data-testid={`backfill-group-cost-${product_name}`}
                              />
                            </div>
                            <div className="col-span-2">
                              <SuggestInput
                                value={groupDraft.vendor_name || ""}
                                onChange={(val) => updateBulkGroupDraft(product_name, "vendor_name", val)}
                                suggestions={suggestions.vendors}
                                placeholder="Vendor"
                                className="bg-slate-900 border-slate-700 text-white text-sm h-9"
                                testId={`backfill-group-vendor-${product_name}`}
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="date"
                                value={groupDraft.purchase_date || ""}
                                onChange={(e) => updateBulkGroupDraft(product_name, "purchase_date", e.target.value)}
                                className="bg-slate-900 border-slate-700 text-white text-sm h-9"
                                data-testid={`backfill-group-date-${product_name}`}
                              />
                            </div>
                            <div className="col-span-1">
                              <Button
                                size="sm"
                                onClick={() => applyBulkGroup(product_name, units)}
                                disabled={isSavingGroup}
                                className="bg-saturn-orange hover:bg-saturn-orange/80 text-white w-full h-9"
                                data-testid={`backfill-group-apply-${product_name}`}
                              >
                                {isSavingGroup
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : `Apply to ${units.length}`}
                              </Button>
                            </div>
                          </div>
                          {/* VAT treatment + net/gross toggle row */}
                          <div className="flex flex-wrap items-center gap-3 text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-400">VAT:</span>
                              <select
                                value={treatment}
                                onChange={(e) => updateBulkGroupDraft(product_name, "vat_treatment", e.target.value)}
                                className="bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1 h-7"
                                data-testid={`backfill-group-vat-treatment-${product_name}`}
                              >
                                <option value="standard">Standard (Irish supplier)</option>
                                <option value="eu_reverse_charge">EU reverse charge</option>
                                <option value="no_vat">No VAT (non-EU / exempt)</option>
                              </select>
                            </div>
                            {treatment === "standard" && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400">Amount is:</span>
                                <div className="inline-flex rounded overflow-hidden border border-slate-700">
                                  <button
                                    type="button"
                                    onClick={() => updateBulkGroupDraft(product_name, "input_mode", "net")}
                                    className={`px-2 py-1 text-xs ${inputMode === "net" ? "bg-saturn-orange text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}
                                    data-testid={`backfill-group-mode-net-${product_name}`}
                                  >
                                    Net (ex-VAT)
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateBulkGroupDraft(product_name, "input_mode", "gross")}
                                    className={`px-2 py-1 text-xs ${inputMode === "gross" ? "bg-saturn-orange text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}
                                    data-testid={`backfill-group-mode-gross-${product_name}`}
                                  >
                                    Gross (VAT-inc)
                                  </button>
                                </div>
                              </div>
                            )}
                            {treatment === "eu_reverse_charge" && (
                              <span className="text-amber-400/80 text-[11px]">
                                Net paid (no supplier VAT). Self-accounted VAT recorded separately at 23%.
                              </span>
                            )}
                            {treatment === "no_vat" && (
                              <span className="text-slate-500 text-[11px]">
                                No VAT involved (non-EU import or genuinely exempt).
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Per-unit override rows — always available for the
                            "price changed between batches" case. */}
                        <div className="divide-y divide-slate-700/50">
                          {units.map((u) => {
                            const draft = backfillDrafts[u.unit_id] || {};
                            return (
                              <div
                                key={u.unit_id}
                                className="px-3 py-2 grid grid-cols-12 gap-2 items-center"
                                data-testid={`backfill-row-${u.unit_id}`}
                              >
                                <div className="col-span-4 min-w-0 pl-2">
                                  <div className="text-xs text-slate-400 truncate flex items-center gap-1.5">
                                    Override this row only
                                    {u.is_depleted && (
                                      <span
                                        className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300"
                                        data-testid={`backfill-depleted-badge-${u.unit_id}`}
                                      >
                                        depleted
                                      </span>
                                    )}
                                    {u.jobs_affected > 0 && (
                                      <span
                                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300"
                                        data-testid={`backfill-jobs-affected-${u.unit_id}`}
                                        title="Jobs that already used this unit and are understating their material cost"
                                      >
                                        {u.jobs_affected} job{u.jobs_affected === 1 ? "" : "s"}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-600 font-mono truncate">
                                    {u.unit_id}
                                    {u.purchase_date ? ` · ${u.purchase_date}` : ""}
                                    {u.vendor_name ? ` · ${u.vendor_name}` : ""}
                                  </div>
                                </div>
                                <div className="col-span-3">
                                  <Input
                                    type="number" min="0" step="0.01"
                                    value={draft.cost || ""}
                                    onChange={(e) => updateBackfillDraft(u.unit_id, "cost", e.target.value)}
                                    placeholder="€ (override)"
                                    className="bg-slate-800 border-slate-700 text-white text-sm h-8"
                                    data-testid={`backfill-cost-${u.unit_id}`}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <SuggestInput
                                    value={draft.vendor_name || u.vendor_name || ""}
                                    onChange={(val) => updateBackfillDraft(u.unit_id, "vendor_name", val)}
                                    suggestions={suggestions.vendors}
                                    placeholder="Vendor"
                                    className="bg-slate-800 border-slate-700 text-white text-sm h-8"
                                    testId={`backfill-vendor-${u.unit_id}`}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Input
                                    type="date"
                                    value={draft.purchase_date || u.purchase_date || ""}
                                    onChange={(e) => updateBackfillDraft(u.unit_id, "purchase_date", e.target.value)}
                                    className="bg-slate-800 border-slate-700 text-white text-sm h-8"
                                  />
                                </div>
                                <div className="col-span-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => saveBackfillRow(u)}
                                    disabled={backfillSavingId === u.unit_id}
                                    className="border-slate-600 text-slate-200 w-full h-8"
                                    data-testid={`backfill-save-${u.unit_id}`}
                                    title="Save just this row"
                                  >
                                    {backfillSavingId === u.unit_id
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : "Save"}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBackfillModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Level modal — per-unit remaining_quantity override.
          Mounted here (not inline per product row) so its state is scoped
          to the whole inventory tab and one modal instance handles every
          product. */}
      <AdjustStockLevelModal
        product={adjustingProduct}
        open={!!adjustingProduct}
        onClose={() => setAdjustingProduct(null)}
        onAdjusted={loadData}
      />

      {/* Hard-Delete confirmation. Shown ONLY for archived products. The
          preview text is dynamic based on how many units will cascade —
          so an admin never blind-clicks through a mass-wipe. */}
      <Dialog
        open={!!hardDeleteTarget}
        onOpenChange={(open) => { if (!open) { setHardDeleteTarget(null); setHardDeletePreview(null); } }}
      >
        <DialogContent className="bg-slate-900 border-red-500/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete permanently?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will remove <span className="text-white font-medium">{hardDeleteTarget?.name}</span> from the database entirely.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-sm">
              <p className="text-red-300 mb-2 font-medium flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                This is irreversible
              </p>
              <ul className="text-slate-300 space-y-1 text-xs">
                <li>• The product row will be permanently removed</li>
                {hardDeletePreview?.unit_count > 0 && (
                  <li className="text-amber-300">
                    • <strong>{hardDeletePreview.unit_count} unit{hardDeletePreview.unit_count === 1 ? "" : "s"}</strong> will be deleted (cascade)
                  </li>
                )}
                {hardDeletePreview?.unit_count === 0 && (
                  <li className="text-slate-400">• No units are attached to this product</li>
                )}
                {hardDeletePreview === null && (
                  <li className="text-slate-500 italic">
                    <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                    Checking attached units…
                  </li>
                )}
                <li className="text-emerald-300/80">
                  • Historical job records are <strong>safe</strong> — product name is snapshotted into each material line at consumption time
                </li>
              </ul>
              <p className="text-slate-400 text-xs mt-2">
                If any unit was already consumed by a completed job, the server will refuse the delete and tell you which unit(s).
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setHardDeleteTarget(null); setHardDeletePreview(null); }}
              disabled={isHardDeleting}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleHardDelete}
              disabled={isHardDeleting || hardDeletePreview === null}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-hard-delete"
            >
              {isHardDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StockInventorySection;
